import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import AdminLayout from './components/Layout/AdminLayout';
import LoginForm from './components/Auth/LoginForm';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import DevicesList from './pages/Devices/DevicesList';
import UsersList from './pages/Users/UsersList';
import AppUsersList from './pages/Users/AppUsersList';
import UserTransactions from './pages/Users/UserTransactions';
import PermissionsList from './pages/Users/PermissionsList';
import ContentManagement from './pages/Content/ContentManagement';
import CategoriesList from './pages/Categories/CategoriesList';
import Settings from './pages/Settings/Settings';
import Notifications from './pages/Notifications/Notifications';
import SwapAnalytics from './pages/Analytics/SwapAnalytics';
import CreativeLookAnalytics from './pages/Analytics/CreativeLookAnalytics';
import CreativeLookOutputImages from './pages/Analytics/CreativeLookOutputImages';
import PurchaseAnalytics from './pages/Analytics/PurchaseAnalytics';
import OffersConfig from './pages/Offers/OffersConfig';
import OutputImages from './pages/Analytics/OutputImages';
import QueueManagement from './pages/Queue/QueueManagement';
import FAQList from './pages/FAQ/FAQList';
import FeaturedAppsList from './pages/FeaturedApps/FeaturedAppsList';
import { CreativeLooksList } from './pages/CreativeLooks';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import ContactUs from './pages/ContactUs';
import NsfwMonitoring from './pages/NsfwMonitoring';
import FlaggedReports from './pages/FlaggedReports';
import AgeDetectionTest from './pages/AgeDetectionTest';
import NsfwTest from './pages/NsfwTest';

// Create theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Public Route Component
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginForm />
                  </PublicRoute>
                }
              />
              <Route
                path="/privacy_policy"
                element={<PrivacyPolicy />}
              />
              <Route
                path="/terms_and_conditions"
                element={<TermsConditions />}
              />
              <Route
                path="/contact_us"
                element={<ContactUs />}
              />

              {/* Protected Routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/devices"
                element={
                  <ProtectedRoute>
                    <DevicesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute>
                    <div>Analytics Page - Coming Soon</div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <UsersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/app-users"
                element={
                  <ProtectedRoute>
                    <AppUsersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/app-users/:userId/transactions"
                element={
                  <ProtectedRoute>
                    <UserTransactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/permissions"
                element={
                  <ProtectedRoute>
                    <PermissionsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <ProtectedRoute>
                    <ContentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <ProtectedRoute>
                    <CategoriesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/creative-looks"
                element={
                  <ProtectedRoute>
                    <CreativeLooksList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/featured-apps"
                element={
                  <ProtectedRoute>
                    <FeaturedAppsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit-logs"
                element={
                  <ProtectedRoute>
                    <div>Audit Logs Page - Coming Soon</div>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/monitor/nsfw"
                element={
                  <ProtectedRoute>
                    <NsfwMonitoring />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/monitor/flagged-reports"
                element={
                  <ProtectedRoute>
                    <FlaggedReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/age-test"
                element={
                  <ProtectedRoute>
                    <AgeDetectionTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/monitor/nsfw-test"
                element={
                  <ProtectedRoute>
                    <NsfwTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics/swaps"
                element={
                  <ProtectedRoute>
                    <SwapAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics/purchases"
                element={
                  <ProtectedRoute>
                    <PurchaseAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics/creative-looks"
                element={
                  <ProtectedRoute>
                    <CreativeLookAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics/creative-look-results"
                element={
                  <ProtectedRoute>
                    <CreativeLookOutputImages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/offers"
                element={
                  <ProtectedRoute>
                    <OffersConfig />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics/output-images"
                element={
                  <ProtectedRoute>
                    <OutputImages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/queue"
                element={
                  <ProtectedRoute>
                    <QueueManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/faqs"
                element={
                  <ProtectedRoute>
                    <FAQList />
                  </ProtectedRoute>
                }
              />

              {/* Default redirects */}
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4caf50',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#f44336',
                secondary: '#fff',
              },
            },
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;